import { ArrowDownTray } from "@ignite-analytics/icons";
import { Button, Stack, Tooltip, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";
import React from "react";
import { FormattedMessage } from "react-intl";

import { middleEllipsis } from "../../../globals/helpers/format";
import useToken from "../../../hooks/useToken";
import { useAlert } from "../../../lib/Alerts";
import { authHeader, formsFetch, unwrap } from "../../../lib/formsApi";
import { guessContentType } from "../../Files/files";

type Props = {
    fileName: string;
    questionId: string;
};

const File: React.FC<Props> = ({ fileName, questionId }) => {
    const { token } = useToken();
    const { alertUser } = useAlert();

    const downloadFile = async () => {
        try {
            const { url } = unwrap(
                await formsFetch.POST("/api/files/download-url", {
                    headers: authHeader(token),
                    body: { referenceId: questionId, fileName },
                }),
                "create download url"
            );
            const response = await fetch(url, {
                headers: {
                    "Content-Type": guessContentType(fileName),
                    "X-Content-Type-Options": "nosniff",
                },
            });
            if (!response.ok) {
                throw new Error(`Download request failed with status ${response.status}`);
            }
            const blobUrl = window.URL.createObjectURL(await response.blob());
            const link = document.createElement("a");
            link.href = blobUrl;
            link.setAttribute("download", `${fileName}`);
            link.setAttribute("disposition-type", "attachment");
            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);
        } catch (downloadError) {
            Sentry.captureException(downloadError, {
                extra: { fileName, questionId, context: "File download error" },
            });
            alertUser({
                value: <FormattedMessage defaultMessage="Could not download the file. Please try again." />,
                severity: "error",
            });
        }
    };

    return (
        <Stack paddingLeft={1} paddingBottom={0.5}>
            <Tooltip title={fileName}>
                <Button
                    color="primary"
                    size="xsmall"
                    startIcon={<ArrowDownTray fontSize="small" />}
                    onClick={downloadFile}
                >
                    <Typography alignContent="center" variant="textSm">
                        {middleEllipsis(fileName, 22, 5)}
                    </Typography>
                </Button>
            </Tooltip>
        </Stack>
    );
};

export default File;
